// Sugalarm — small modal for editing a logged treatment (cycle 34.20).
//
// Pre-fills the numeric value + notes from the row the user tapped,
// then PATCHes /api/treatments/{id}. The server broadcasts the
// existing cycle-24 `treatment` WS frame; the reducer upserts by id,
// so every consumer (BolusRings IOB timer, recent-entries strip,
// dashboard cards) updates in place.
//
// Scope: edits `carbs_g` (carbs), `insulin_u` (insulin / handled
// specially when bg_check/exercise are in `notes`), and `notes`. Does
// NOT change `event_type` or `timestamp` — those are baked in at
// log time and editing them is out of scope for cycle 34.

import { useEffect, useMemo, useState } from "react";

import * as treatmentsApi from "../../api/treatments";
import type { Treatment, TreatmentUpdateIn } from "../../lib/types";

interface Props {
  treatment: Treatment | null;
  onClose: () => void;
  /** Called after a successful PATCH so the parent can refresh its
   *  local cache. The WS frame handles every other consumer. */
  onSaved?: (updated: Treatment) => void;
}

function initialValue(t: Treatment): string {
  if (t.event_type === "carbs" && t.carbs_g != null) {
    return String(t.carbs_g);
  }
  if (t.event_type === "insulin" && t.insulin_u != null) {
    return String(t.insulin_u);
  }
  return "";
}

function valueUnit(eventType: string): string {
  if (eventType === "carbs") return "g";
  if (eventType === "insulin") return "u";
  return "";
}

function valueLabel(eventType: string): string {
  if (eventType === "carbs") return "Carbs";
  if (eventType === "insulin") return "Insulin";
  if (eventType === "bg_check") return "BG check";
  if (eventType === "exercise") return "Exercise";
  return "Note";
}

export function EditTreatmentModal({ treatment, onClose, onSaved }: Props) {
  const [value, setValue] = useState<string>("");
  const [notes, setNotes] = useState<string>("");
  const [busy, setBusy] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [mounted, setMounted] = useState<boolean>(treatment != null);
  const visible = treatment != null;

  // Reset form when the row identity changes (modal re-opened on a
  // different entry). Track the id in a separate effect dep so we
  // don't fight React's setState batching mid-edit.
  const rowId = treatment?.id ?? null;
  useEffect(() => {
    if (treatment) {
      setValue(initialValue(treatment));
      setNotes(treatment.notes ?? "");
      setError(null);
    }
  }, [rowId, treatment]);

  useEffect(() => {
    if (visible) {
      setMounted(true);
      return;
    }
    const t = window.setTimeout(() => setMounted(false), 220);
    return () => window.clearTimeout(t);
  }, [visible]);

  const supportsValueEdit = useMemo(() => {
    if (!treatment) return false;
    return treatment.event_type === "carbs" || treatment.event_type === "insulin";
  }, [treatment]);

  if (!mounted || !treatment) return null;

  const onSave = async () => {
    setError(null);
    const body: TreatmentUpdateIn = {};
    if (supportsValueEdit) {
      const n = Number(value);
      if (value === "" || !Number.isFinite(n) || n < 0) {
        setError("Enter a valid number.");
        return;
      }
      if (treatment.event_type === "insulin" && n <= 0) {
        setError("Insulin must be greater than 0.");
        return;
      }
      if (treatment.event_type === "carbs") body.carbs_g = n;
      if (treatment.event_type === "insulin") body.insulin_u = n;
    }
    const trimmed = notes.trim();
    body.notes = trimmed.length > 0 ? trimmed : null;

    setBusy(true);
    const r = await treatmentsApi.update(treatment.id, body);
    setBusy(false);
    if (!r.ok) {
      setError(r.error.message);
      return;
    }
    onSaved?.(r.data);
    onClose();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Edit entry"
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 360,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: visible ? "rgba(0, 0, 0, 0.32)" : "transparent",
        backdropFilter: "blur(3px)",
        transition: "background 220ms ease-out",
        pointerEvents: visible ? "auto" : "none",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: "min(360px, 100%)",
          padding: "22px 22px 18px",
          background: "var(--bg-elev)",
          border: "0.5px solid var(--line-strong)",
          borderRadius: 18,
          boxShadow: "0 16px 48px rgba(0, 0, 0, 0.4)",
          transform: visible
            ? "scale(1) translateY(0)"
            : "scale(0.94) translateY(8px)",
          opacity: visible ? 1 : 0,
          transition: "transform 220ms ease-out, opacity 200ms ease-out",
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div
          style={{
            fontSize: "var(--type-floor)",
            fontWeight: 700,
            letterSpacing: "0.10em",
            textTransform: "uppercase",
            color: "var(--ink-3)",
          }}
        >
          Edit {valueLabel(treatment.event_type).toLowerCase()}
        </div>

        {supportsValueEdit && (
          <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            <span style={{ fontSize: "var(--type-floor)", color: "var(--ink-2)" }}>
              {valueLabel(treatment.event_type)}
              {valueUnit(treatment.event_type) && (
                <span style={{ color: "var(--ink-3)" }}>
                  {" "}
                  ({valueUnit(treatment.event_type)})
                </span>
              )}
            </span>
            <input
              type="number"
              inputMode="decimal"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              autoFocus
              min={0}
              step={treatment.event_type === "insulin" ? "0.1" : "1"}
              style={{
                appearance: "none",
                border: "0.5px solid var(--line-strong)",
                background: "var(--bg)",
                color: "var(--ink)",
                borderRadius: 10,
                padding: "10px 12px",
                fontSize: 18,
                fontFamily: "var(--hero-font)",
                fontVariantNumeric: "tabular-nums",
                outline: "none",
              }}
            />
          </label>
        )}

        <label style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <span style={{ fontSize: "var(--type-floor)", color: "var(--ink-2)" }}>Notes</span>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={200}
            placeholder={treatment.event_type === "note" ? "What happened?" : "Optional"}
            style={{
              appearance: "none",
              border: "0.5px solid var(--line-strong)",
              background: "var(--bg)",
              color: "var(--ink)",
              borderRadius: 10,
              padding: "10px 12px",
              fontSize: 14,
              outline: "none",
            }}
          />
        </label>

        {error && (
          <div style={{ color: "var(--zone-urgent)", fontSize: "var(--type-floor)", fontWeight: 500 }}>
            {error}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              appearance: "none",
              flex: 1,
              border: "0.5px solid var(--line-strong)",
              background: "transparent",
              color: "var(--ink-2)",
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onSave}
            disabled={busy}
            style={{
              appearance: "none",
              flex: 1,
              border: "0.5px solid var(--accent)",
              background: "var(--accent-soft)",
              color: "var(--accent-ink)",
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
